import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  useChartConfigsQuery,
  useComputeStatsLazyQuery,
  useTransactionValuesForSelectQuery,
  useEditStatMutation,
  useAddStatToUserMutation,
  ChartConfigsDocument,
} from "../generated/graphql";
import ErrorMessage from "./ErrorMessage";
import Toast from "./Toast";
import { useToast } from "../hooks/useToast";
import { extractPostIdFromComposite, parsePostId } from "../utils/idHelpers";

// Générer un ID temporaire au format ObjectId MongoDB (24 caractères hexadécimaux)
const generateTempId = (): string => {
  const chars = "0123456789abcdef";
  let result = "";
  for (let i = 0; i < 24; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

// Couleurs des graphiques Recharts — CSS custom properties définies dans index.css.
// Recharts n'accepte pas les classes Tailwind dans fill/stroke ; les CSS vars sont la solution conforme.
const COLORS = [
  "var(--chart-color-1)",
  "var(--chart-color-2)",
  "var(--chart-color-3)",
  "var(--chart-color-4)",
  "var(--chart-color-5)",
  "var(--chart-color-6)",
  "var(--chart-color-7)",
  "var(--chart-color-8)",
  "var(--chart-color-9)",
  "var(--chart-color-10)",
];

// Composant Tooltip personnalisé pour afficher le total
interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
    payload: Record<string, unknown>;
  }>;
  label?: string | number;
  formatCurrency: (value: number) => string;
}

const CustomTooltip = ({
  active,
  payload,
  label,
  formatCurrency,
}: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  // Calculer le total de toutes les séries pour cette année
  const payloadData = payload[0]?.payload as Record<string, unknown>;
  const total = Object.keys(payloadData)
    .filter((key) => key !== "year")
    .reduce((sum, key) => {
      const val = payloadData[key];
      return sum + (typeof val === "number" ? val : 0);
    }, 0);

  return (
    <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3">
      <p className="font-semibold text-gray-900 mb-2">Année {label}</p>
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center gap-2">
            {/* style= obligatoire : entry.color est injecté dynamiquement par Recharts */}
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-700">{entry.name}:</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrency(entry.value as number)}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-gray-200">
        <div className="flex justify-between items-center">
          <span className="text-sm font-semibold text-gray-900">Total:</span>
          <span className="text-sm font-bold text-gray-900">{formatCurrency(total)}</span>
        </div>
      </div>
    </div>
  );
};

const Stats: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const [selectedStatId, setSelectedStatId] = useState<string>("");
  const [editingContexts, setEditingContexts] = useState<
    Array<{ id: string; contextId: string; postId: string }>
  >([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newStatName, setNewStatName] = useState<string>("");
  const [newStatContexts, setNewStatContexts] = useState<
    Array<{ id: string; contextId: string; postId: string }>
  >([{ id: generateTempId(), contextId: "", postId: "" }]);
  const { toast, setToast } = useToast();

  const { loading, data, error, refetch } = useChartConfigsQuery({
    skip: !isAuthenticated,
  });

  const {
    data: valuesData,
  } = useTransactionValuesForSelectQuery({
    skip: !isAuthenticated,
  });

  const [editStat, { loading: editLoading }] = useEditStatMutation();
  const [addStatToUser, { loading: addLoading }] = useAddStatToUserMutation();

  // Construire l'input pour computeStats à partir de la stat sélectionnée ou des modifications en cours
  const chartDataInput = useMemo(() => {
    // Si on est en mode création et que toutes les lignes sont complètes, utiliser newStatContexts
    if (isCreating && newStatContexts.length > 0) {
      const allComplete = newStatContexts.every((ctx) => ctx.contextId && ctx.postId);
      if (allComplete) {
        return {
          contexts: newStatContexts.map((ctx) => {
            // Extraire l'ID réel du post si c'est un ID composite
            const realPostId = ctx.postId.includes('/')
              ? extractPostIdFromComposite(ctx.postId)
              : ctx.postId;
            return {
              postId: realPostId,
              contextId: ctx.contextId,
            };
          }),
        };
      }
      return undefined;
    }

    // Si on est en mode édition et que toutes les lignes sont complètes, utiliser editingContexts
    if (isEditing && editingContexts.length > 0) {
      const allComplete = editingContexts.every((ctx) => ctx.contextId && ctx.postId);
      if (allComplete) {
        return {
          contexts: editingContexts.map((ctx) => {
            // Extraire l'ID réel du post si c'est un ID composite
            const realPostId = ctx.postId.includes('/')
              ? extractPostIdFromComposite(ctx.postId)
              : ctx.postId;
            return {
              postId: realPostId,
              contextId: ctx.contextId,
            };
          }),
        };
      }
      return undefined;
    }

    // Sinon, utiliser la stat sélectionnée
    if (!selectedStatId || !data?.currentUser?.stats) {
      return undefined;
    }

    const selectedStat = data.currentUser.stats.find((s) => s.id === selectedStatId);
    if (!selectedStat || !selectedStat.contexts || selectedStat.contexts.length === 0) {
      return undefined;
    }

    return {
      contexts: selectedStat.contexts.map((ctx) => {
        // Extraire l'ID réel du post si c'est un ID composite
        const realPostId = ctx.post.id.includes('/') 
          ? extractPostIdFromComposite(ctx.post.id)
          : ctx.post.id;
        return {
          postId: realPostId,
          contextId: ctx.context.id,
        };
      }),
    };
  }, [selectedStatId, data, isEditing, editingContexts, isCreating, newStatContexts]);

  const [
    computeStats,
    { loading: computeLoading, data: computeData, error: computeError },
  ] = useComputeStatsLazyQuery();

  // Appeler computeStats quand une stat est sélectionnée ou quand la config est modifiée
  useEffect(() => {
    if (chartDataInput && isAuthenticated) {
      computeStats({
        variables: {
          input: chartDataInput,
        },
      });
    }
  }, [chartDataInput, isAuthenticated, computeStats]);

  // Transformer les données pour Recharts (graphique en barres empilées)
  const chartData = useMemo(() => {
    if (!computeData?.chartData?.contexts) {
      return [];
    }

    // Grouper par année
    const dataByYear: Record<
      number,
      Record<string, number> & { year: number }
    > = {};

    // Créer une liste unique des combinaisons context+post
    const seriesKeys = new Set<string>();

    computeData.chartData.contexts.forEach((ctx) => {
      const key = `${ctx.context.name} - ${ctx.post.name}`;
      seriesKeys.add(key);

      if (!dataByYear[ctx.year]) {
        dataByYear[ctx.year] = { year: ctx.year };
      }

      // Convertir les centimes en euros
      dataByYear[ctx.year][key] = (dataByYear[ctx.year][key] || 0) + ctx.amount / 100;
    });

    // Convertir en tableau et trier par année
    return Object.values(dataByYear).sort((a, b) => a.year - b.year);
  }, [computeData]);

  // Formater les montants dans le tooltip
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Récupérer les noms des contexts et posts à partir des groupes
  // Utiliser data.groups en priorité pour garantir la correspondance des IDs
  const selectedStatContextsAndPosts = useMemo(() => {
    if (!selectedStatId || !data?.currentUser?.stats) {
      return [];
    }

    const selectedStat = data.currentUser.stats.find((s) => s.id === selectedStatId);
    if (!selectedStat || !selectedStat.contexts) {
      return [];
    }

    // Utiliser data.groups en priorité, sinon valuesData.groups en fallback
    const groups = data?.groups || valuesData?.groups;
    if (!groups) {
      // Si pas de groupes disponibles, utiliser les noms directement depuis la stat
      return selectedStat.contexts.map((ctx) => ({
        id: ctx.id,
        contextId: ctx.context.id,
        postId: ctx.post.id,
        contextName: ctx.context.name,
        postName: ctx.post.name,
      }));
    }

    // Créer un map pour retrouver rapidement les noms par ID
    const contextMap = new Map<string, { contextName: string; postName: string }>();

    groups.forEach((group) => {
      group.contexts.forEach((context) => {
        context.posts.forEach((post) => {
          // Les IDs sont déjà transformés en composites par le Apollo Link
          // On utilise directement post.id qui est déjà au format foyerId/contextId/postId
          const key = `${context.id}-${post.id}`;
          contextMap.set(key, {
            contextName: context.name,
            postName: post.name,
          });
        });
      });
    });

    // Mapper les contexts de la stat avec leurs noms
    return selectedStat.contexts.map((ctx) => {
      // Les IDs de post sont déjà transformés en composites par le Apollo Link
      const postId = ctx.post.id;
      const key = `${ctx.context.id}-${postId}`;
      const names = contextMap.get(key);
      return {
        id: ctx.id,
        contextId: ctx.context.id,
        postId: postId, // Utiliser l'ID composite (déjà transformé)
        contextName: names?.contextName || ctx.context.name,
        postName: names?.postName || ctx.post.name,
      };
    });
  }, [selectedStatId, data, valuesData]);

  // Récupérer tous les contexts et posts disponibles pour les selects
  // Utiliser data.groups (de chartConfigs) pour garantir que les IDs correspondent exactement
  // à ceux utilisés dans les stats, sinon utiliser valuesData.groups en fallback
  const availableContextsAndPosts = useMemo(() => {
    const groups = data?.groups || valuesData?.groups;
    if (!groups) return { contexts: [], posts: [] };

    const contextsMap = new Map<string, { id: string; name: string; posts: Array<{ id: string; name: string }> }>();

    groups.forEach((group) => {
      group.contexts.forEach((context) => {
        if (!contextsMap.has(context.id)) {
          contextsMap.set(context.id, {
            id: context.id,
            name: context.name,
            posts: [],
          });
        }
        const contextData = contextsMap.get(context.id)!;
        context.posts.forEach((post) => {
          // Les IDs sont déjà transformés en composites par le Apollo Link
          // On utilise directement post.id qui est déjà au format foyerId/contextId/postId
          if (!contextData.posts.find((p) => p.id === post.id)) {
            contextData.posts.push({ id: post.id, name: post.name });
          }
        });
      });
    });

    return {
      contexts: Array.from(contextsMap.values()),
      posts: Array.from(contextsMap.values()).flatMap((ctx) => ctx.posts),
    };
  }, [data, valuesData]);

  // Fonction helper pour trouver le post correspondant même si le groupId diffère
  const findMatchingPostId = useMemo(() => {
    return (targetPostId: string, targetContextId: string): string | null => {
      // Si l'ID est déjà dans availableContextsAndPosts, le retourner directement
      const context = availableContextsAndPosts.contexts.find((c) => c.id === targetContextId);
      if (context) {
        const exactMatch = context.posts.find((p) => p.id === targetPostId);
        if (exactMatch) {
          return exactMatch.id;
        }
        
        // Si pas de correspondance exacte, essayer de trouver par contextId et postId réel
        try {
          const targetParsed = parsePostId(targetPostId);
          const matchingPost = context.posts.find((p) => {
            try {
              const parsed = parsePostId(p.id);
              return parsed.contextId === targetParsed.contextId && parsed.postId === targetParsed.postId;
            } catch {
              return false;
            }
          });
          if (matchingPost) {
            return matchingPost.id;
          }
        } catch {
          // Si l'ID n'est pas composite, retourner null
        }
      }
      return null;
    };
  }, [availableContextsAndPosts]);

  // Initialiser editingContexts quand une stat est sélectionnée
  useEffect(() => {
    if (selectedStatContextsAndPosts.length > 0 && !isEditing && availableContextsAndPosts.contexts.length > 0) {
      // Mapper les contexts en s'assurant que les IDs correspondent
      const mappedContexts = selectedStatContextsAndPosts.map((item) => {
        // Vérifier que le contextId existe dans availableContextsAndPosts
        const contextExists = availableContextsAndPosts.contexts.some((c) => c.id === item.contextId);
        if (!contextExists) {
          console.warn(`Context ID ${item.contextId} not found in available contexts`);
          return {
            id: item.id,
            contextId: item.contextId,
            postId: item.postId,
          };
        }
        
        // Trouver le postId correspondant (même si le groupId diffère)
        const matchingPostId = findMatchingPostId(item.postId, item.contextId);
        const finalPostId = matchingPostId || item.postId;
        
        return {
          id: item.id,
          contextId: item.contextId,
          postId: finalPostId,
        };
      });
      setEditingContexts(mappedContexts);
    }
  }, [selectedStatContextsAndPosts, isEditing, availableContextsAndPosts, findMatchingPostId]);

  // Obtenir les posts disponibles pour un context donné
  const getPostsForContext = (contextId: string) => {
    const context = availableContextsAndPosts.contexts.find((c) => c.id === contextId);
    return context?.posts || [];
  };

  // Gérer le changement d'un context ou post
  const handleContextChange = (index: number, field: "contextId" | "postId", value: string) => {
    const newEditingContexts = [...editingContexts];
    newEditingContexts[index] = {
      ...newEditingContexts[index],
      [field]: value,
      // Si on change le context, réinitialiser le post
      ...(field === "contextId" ? { postId: "" } : {}),
    };
    setEditingContexts(newEditingContexts);
  };

  // Ajouter une nouvelle ligne
  const handleAddRow = () => {
    setEditingContexts([
      ...editingContexts,
      { id: generateTempId(), contextId: "", postId: "" },
    ]);
  };

  // Supprimer une ligne
  const handleRemoveRow = (index: number) => {
    setEditingContexts(editingContexts.filter((_, i) => i !== index));
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    if (!selectedStatId || !data?.currentUser?.stats) return;

    const selectedStat = data.currentUser.stats.find((s) => s.id === selectedStatId);
    if (!selectedStat) return;

    // Valider que tous les champs sont remplis
    if (editingContexts.some((ctx) => !ctx.contextId || !ctx.postId)) {
      setToast({
        message: "Veuillez remplir tous les champs (context et post) pour chaque ligne",
        type: "error",
      });
      return;
    }

    try {
      await editStat({
        variables: {
          id: selectedStatId,
          stat: {
            id: selectedStatId,
            name: selectedStat.name,
            contexts: editingContexts.map((ctx) => {
              // Vérifier si c'est un ID existant dans la stat originale
              const originalContext = selectedStat.contexts?.find((c) => c.id === ctx.id);
              // Extraire l'ID réel du post si c'est un ID composite
              const realPostId = ctx.postId.includes('/')
                ? extractPostIdFromComposite(ctx.postId)
                : ctx.postId;
              // Inclure l'ID existant ou générer un nouvel ID pour les nouveaux contexts
              return {
                contextId: ctx.contextId,
                postId: realPostId,
                id: originalContext ? ctx.id : generateTempId(),
              };
            }),
          },
        },
        refetchQueries: [{ query: ChartConfigsDocument }],
      });

      setToast({ message: "Statistique mise à jour avec succès", type: "success" });
      setIsEditing(false);
    } catch (err: any) {
      console.error("Erreur lors de la mise à jour de la statistique:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la mise à jour";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  // Gérer l'ajout d'une nouvelle stat
  const handleAddNewStat = () => {
    setIsCreating(true);
    setNewStatName("");
    setNewStatContexts([{ id: generateTempId(), contextId: "", postId: "" }]);
    setSelectedStatId("");
    setIsEditing(false);
  };

  const handleCancelNewStat = () => {
    setIsCreating(false);
    setNewStatName("");
    setNewStatContexts([{ id: generateTempId(), contextId: "", postId: "" }]);
  };

  const handleAddNewStatContext = () => {
    setNewStatContexts([
      ...newStatContexts,
      { id: generateTempId(), contextId: "", postId: "" },
    ]);
  };

  const handleRemoveNewStatContext = (index: number) => {
    setNewStatContexts(newStatContexts.filter((_, i) => i !== index));
  };

  const handleNewStatContextChange = (
    index: number,
    field: "contextId" | "postId",
    value: string
  ) => {
    const newContexts = [...newStatContexts];
    newContexts[index] = {
      ...newContexts[index],
      [field]: value,
      ...(field === "contextId" ? { postId: "" } : {}),
    };
    setNewStatContexts(newContexts);
  };

  const handleSaveNewStat = async () => {
    // Valider le nom
    if (!newStatName.trim()) {
      setToast({
        message: "Veuillez saisir un nom pour la statistique",
        type: "error",
      });
      return;
    }

    // Valider que tous les champs sont remplis
    if (newStatContexts.some((ctx) => !ctx.contextId || !ctx.postId)) {
      setToast({
        message: "Veuillez remplir tous les champs (context et post) pour chaque ligne",
        type: "error",
      });
      return;
    }

    // Valider qu'il y a au moins une ligne
    if (newStatContexts.length === 0) {
      setToast({
        message: "Veuillez ajouter au moins un context et un post",
        type: "error",
      });
      return;
    }

    try {
      const result = await addStatToUser({
        variables: {
          stat: {
            id: generateTempId(), // Nouvelle stat, générer un ID temporaire
            name: newStatName.trim(),
            contexts: newStatContexts.map((ctx) => {
              // Extraire l'ID réel du post si c'est un ID composite
              const realPostId = ctx.postId.includes('/')
                ? extractPostIdFromComposite(ctx.postId)
                : ctx.postId;
              return {
                contextId: ctx.contextId,
                postId: realPostId,
                id: generateTempId(), // Nouveau context, générer un ID temporaire
              };
            }),
          },
        },
        refetchQueries: [{ query: ChartConfigsDocument }],
      });

      // Sélectionner la nouvelle stat créée
      if (result.data?.addStatToUser?.stats) {
        const newStat = result.data.addStatToUser.stats.find(
          (s) => s.name === newStatName.trim()
        );
        if (newStat) {
          setSelectedStatId(newStat.id);
        }
      }

      setToast({ message: "Statistique créée avec succès", type: "success" });
      setIsCreating(false);
      setNewStatName("");
      setNewStatContexts([{ id: generateTempId(), contextId: "", postId: "" }]);
    } catch (err: any) {
      console.error("Erreur lors de la création de la statistique:", err);
      const errorMessage = err?.message || "Une erreur est survenue lors de la création";
      setToast({ message: errorMessage, type: "error" });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Statistiques</h1>
        <div className="text-center text-gray-500">Chargement...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Statistiques</h1>
        <div className="text-center text-red-500">
          Veuillez vous connecter pour accéder aux statistiques
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Statistiques</h1>
        <ErrorMessage
          error={error}
          onRetry={() => refetch()}
          title="Erreur lors du chargement des statistiques"
          className="mb-6"
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto">
        <h1 className="text-2xl font-bold mb-6">Statistiques</h1>
        <ErrorMessage
          onRetry={() => refetch()}
          title="Erreur lors du chargement des statistiques - pas de données"
          className="mb-6"
        />
      </div>
    );
  }

  const stats = data.currentUser?.stats || [];

  return (
    <>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="container mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Statistiques</h1>
        {!isCreating && (
          <button
            onClick={handleAddNewStat}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors"
          >
            + Nouvelle statistique
          </button>
        )}
      </div>

      {isCreating ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Créer une nouvelle statistique</h2>
          
          <div className="mb-4">
            <label htmlFor="new-stat-name" className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la statistique :
            </label>
            <input
              id="new-stat-name"
              type="text"
              value={newStatName}
              onChange={(e) => setNewStatName(e.target.value)}
              placeholder="Ex: Dépenses mensuelles"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contexts et Posts :
            </label>
            <div className="space-y-3">
              {newStatContexts.map((ctx, index) => (
                <div
                  key={ctx.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
                >
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Context
                      </label>
                      <select
                        value={ctx.contextId}
                        onChange={(e) =>
                          handleNewStatContextChange(index, "contextId", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Choisir un context --</option>
                        {availableContextsAndPosts.contexts.map((context) => (
                          <option key={context.id} value={context.id}>
                            {context.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Post
                      </label>
                      <select
                        value={ctx.postId}
                        onChange={(e) =>
                          handleNewStatContextChange(index, "postId", e.target.value)
                        }
                        disabled={!ctx.contextId}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      >
                        <option value="">-- Choisir un post --</option>
                        {getPostsForContext(ctx.contextId).map((post) => (
                          <option key={post.id} value={post.id}>
                            {post.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {newStatContexts.length > 1 && (
                    <button
                      onClick={() => handleRemoveNewStatContext(index)}
                      className="px-2 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-hidden focus:ring-2 focus:ring-red-500"
                      title="Supprimer cette ligne"
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={handleAddNewStatContext}
                className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 border border-blue-200"
              >
                + Ajouter une ligne
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleCancelNewStat}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500"
            >
              Annuler
            </button>
            <button
              onClick={handleSaveNewStat}
              disabled={addLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addLoading ? "Création..." : "Créer la statistique"}
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-6">
          <label htmlFor="stat-select" className="block text-sm font-medium text-gray-700 mb-1">
            Sélectionner une statistique :
          </label>
          <select
            id="stat-select"
            value={selectedStatId}
            onChange={(e) => {
              setSelectedStatId(e.target.value);
              setIsEditing(false); // Fermer l'encart "modifier" quand on sélectionne une stat
            }}
            className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">-- Choisir une statistique --</option>
            {stats.map((stat) => (
              <option key={stat.id} value={stat.id}>
                {stat.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {(selectedStatId || (isCreating && newStatName)) && (
        <div className="bg-white rounded-lg shadow-md p-6">
          {selectedStatId && (
            <p className="text-gray-600 mb-4">
              Statistique sélectionnée :{" "}
              <span className="font-semibold">
                {stats.find((s) => s.id === selectedStatId)?.name}
              </span>
            </p>
          )}
          {isCreating && (
            <p className="text-gray-600 mb-4">
              Nouvelle statistique :{" "}
              <span className="font-semibold">{newStatName || "(sans nom)"}</span>
            </p>
          )}

          {computeLoading && (
            <div className="text-center text-gray-500 py-4">Calcul des données en cours...</div>
          )}

          {computeError && (
            <ErrorMessage
              error={computeError}
              onRetry={() => {
                if (chartDataInput) {
                  computeStats({
                    variables: {
                      input: chartDataInput,
                    },
                  });
                }
              }}
              title="Erreur lors du calcul des statistiques"
              className="mb-4"
            />
          )}

          {((selectedStatContextsAndPosts.length > 0 && !isCreating) || (isCreating && newStatContexts.some((ctx) => ctx.contextId && ctx.postId))) && (
            <div className="mt-4 mb-4 bg-white rounded-lg p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700">
                  Contexts et Posts visualisés :
                </h3>
                {!isEditing && !isCreating ? (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                  >
                    Modifier
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setIsEditing(false);
                        // Réinitialiser les modifications
                        setEditingContexts(
                          selectedStatContextsAndPosts.map((item) => ({
                            id: item.id,
                            contextId: item.contextId,
                            postId: item.postId,
                          }))
                        );
                      }}
                      className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-gray-500"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={editLoading}
                      className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {editLoading ? "Enregistrement..." : "Enregistrer"}
                    </button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-3">
                  {editingContexts.map((ctx, index) => (
                    <div
                      key={ctx.id}
                      className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
                    >
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Context
                          </label>
                          <select
                            value={ctx.contextId}
                            onChange={(e) =>
                              handleContextChange(index, "contextId", e.target.value)
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">-- Choisir un context --</option>
                            {availableContextsAndPosts.contexts.map((context) => (
                              <option key={context.id} value={context.id}>
                                {context.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Post
                          </label>
                          <select
                            value={ctx.postId}
                            onChange={(e) =>
                              handleContextChange(index, "postId", e.target.value)
                            }
                            disabled={!ctx.contextId}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            <option value="">-- Choisir un post --</option>
                            {getPostsForContext(ctx.contextId).map((post) => (
                              <option key={post.id} value={post.id}>
                                {post.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveRow(index)}
                        className="px-2 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 focus:outline-hidden focus:ring-2 focus:ring-red-500"
                        title="Supprimer cette ligne"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={handleAddRow}
                    className="w-full px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 focus:outline-hidden focus:ring-2 focus:ring-blue-500 border border-blue-200"
                  >
                    + Ajouter une ligne
                  </button>
                </div>
              ) : isCreating ? (
                <div className="flex flex-wrap gap-2">
                  {newStatContexts
                    .filter((ctx) => ctx.contextId && ctx.postId)
                    .map((ctx, index) => {
                      const context = availableContextsAndPosts.contexts.find((c) => c.id === ctx.contextId);
                      const post = context?.posts.find((p) => p.id === ctx.postId);
                      return (
                        <span
                          key={`${ctx.contextId}-${ctx.postId}-${index}`}
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300"
                        >
                          {context?.name || ""} - {post?.name || ""}
                        </span>
                      );
                    })}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedStatContextsAndPosts.map((item) => (
                    <span
                      key={`${item.contextId}-${item.postId}`}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300"
                    >
                      {item.contextName} - {item.postName}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {computeData && computeData.chartData && chartData.length > 0 && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-4">Graphique</h3>
              <div className="w-full h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="year"
                      label={{ value: "Année", position: "insideBottom", offset: -5 }}
                    />
                    <YAxis
                      label={{ value: "Montant (€)", angle: -90, position: "insideLeft" }}
                      tickFormatter={(value) => formatCurrency(value)}
                    />
                    <Tooltip
                      content={<CustomTooltip formatCurrency={formatCurrency} />}
                    />
                    <Legend />
                    {Array.from(
                      new Set(
                        computeData.chartData.contexts.map(
                          (ctx) => `${ctx.context.name} - ${ctx.post.name}`
                        )
                      )
                    ).map((key, index) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="a"
                        fill={COLORS[index % COLORS.length]}
                        name={key}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {stats.length === 0 && (
        <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
          <p className="text-gray-500 italic text-center">
            Aucune statistique disponible pour le moment.
          </p>
        </div>
      )}
      </div>
    </>
  );
};

export default Stats;
